import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { AppState, AppStore } from "./store";
import { useCallback, useEffect, useState } from "react";
import { useAppStoreContext } from "./context";

const useAppStoreOnChange = <T extends any>(
  store: AppStore,
  selector: (state: AppState) => T,
  callback: (value: T, previous: T) => void,
): void => {
  const callbackRef = useRefAutoUpdate(callback);
  useEffect(() => {
    const listener = store?.registerSelector(selector, (value, previous) => {
      callbackRef.current(value, previous)
    });
    return () => {
      listener?.flush();
    };
  }, [store, selector]);
};


export const useAppStoreSelector = <T extends any>(
  selector: (state: AppState) => T,
): T => {
  const store = useAppStoreContext();
  const selectorRef = useRefAutoUpdate(selector);

  const getInstantState = () => {
    const currState = store.getState();
    return selectorRef.current(currState);
  };

  const [data, setData] = useState(() => getInstantState());

  const _selector = useCallback((state: AppState) => selector(state), []);
  const _callback = useCallback((value: T) => setData(value), []);

  useAppStoreOnChange(store, _selector, _callback);

  useEffect(() => {
    const state = getInstantState();
    setData(state);
  }, [store]);

  return data;
};